import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { createCheckoutSession, validateDiscount, getStoreSettings } from "../services/api";
import { WaitlistDialog } from "../components/WaitlistDialog";
import Seo from "../components/Seo";
import { Lock, AlertTriangle, ShoppingBag, Tag, X, BellRing, PackageX } from "lucide-react";

const MIN_BOTTLES = 12;

const itemBottles = (c) => (c.custom ? (c.bottles || MIN_BOTTLES) : c.category === "starter" ? MIN_BOTTLES * c.qty : c.qty);

export default function Checkout() {
  const { t, lang, cart, cartTotal } = useApp();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", street: "", zip: "", city: "", country: "CH", note: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [soldOut, setSoldOut] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  React.useEffect(() => {
    getStoreSettings().then((s) => setSoldOut(!!s.sold_out)).catch(() => {});
  }, []);

  const [codeInput, setCodeInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [codeError, setCodeError] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);

  const bottleCount = cart.reduce((s, c) => s + itemBottles(c), 0);
  const tooFew = bottleCount < MIN_BOTTLES;
  const finalTotal = Math.max(cartTotal - (discount?.amount || 0), 0);

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    setCodeError("");
    setCodeLoading(true);
    try {
      const d = await validateDiscount(codeInput, cartTotal);
      setDiscount(d);
      setCodeInput("");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      setCodeError(detail.startsWith("MIN_TOTAL") ? t("checkout.codeMinTotal").replace("{min}", detail.split(":")[1]) : t("checkout.codeInvalid"));
    } finally {
      setCodeLoading(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { url } = await createCheckoutSession({
        items: cart.map((c) => ({ id: c.id, qty: c.qty, bottles: c.bottles, name: c.name, img: c.img })),
        address: form,
        origin_url: window.location.origin,
        discount_code: discount?.code || null,
      });
      window.location.href = url;
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(d === "MIN_BOTTLES" ? t("checkout.minWarning") : (typeof d === "string" ? d : t("checkout.payError")));
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="pt-20 min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center text-ink/60" data-testid="checkout-empty">
          <ShoppingBag className="w-12 h-12 mx-auto text-wine/30 mb-4" />
          <p>{t("common.emptyCart")}</p>
          <Link to="/store"><Button className="mt-6 bg-wine hover:bg-wine-dark text-white">{t("common.continueShopping")}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-cream">
      <Seo title={t("checkout.title")} description={t("checkout.sub")} path="/checkout" lang={lang} noindex />
      <div className="max-w-6xl mx-auto px-6 py-14" data-testid="checkout-page">
        <h1 className="font-serif text-4xl text-ink mb-2">{t("checkout.title")}</h1>
        <p className="text-ink/60 mb-10">{t("checkout.sub")}</p>

        {!user && (
          <div className="bg-gold/10 border border-gold/30 rounded-xl px-5 py-3 mb-8 text-sm text-ink/70" data-testid="checkout-login-hint">
            {t("checkout.loginHint")} <Link to="/login?next=/checkout" className="text-wine font-semibold underline underline-offset-4">{t("auth.loginCta")}</Link>
          </div>
        )}

        <form onSubmit={pay} className="grid lg:grid-cols-5 gap-10">
          {/* Address */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-border p-6 sm:p-8 space-y-4">
            <h2 className="font-serif text-2xl text-wine mb-2">{t("checkout.shipping")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input data-testid="checkout-firstname" value={form.firstName} onChange={set("firstName")} placeholder={t("checkout.firstName")} required />
              <Input data-testid="checkout-lastname" value={form.lastName} onChange={set("lastName")} placeholder={t("checkout.lastName")} required />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input data-testid="checkout-email" type="email" value={form.email} onChange={set("email")} placeholder={t("auth.email")} required />
              <Input data-testid="checkout-phone" value={form.phone} onChange={set("phone")} placeholder={t("checkout.phone")} />
            </div>
            <Input data-testid="checkout-street" value={form.street} onChange={set("street")} placeholder={t("checkout.street")} required />
            <div className="grid grid-cols-[110px_1fr] gap-4">
              <Input data-testid="checkout-zip" value={form.zip} onChange={set("zip")} placeholder={t("checkout.zip")} required />
              <Input data-testid="checkout-city" value={form.city} onChange={set("city")} placeholder={t("checkout.city")} required />
            </div>
            <Input data-testid="checkout-country" value={form.country} onChange={set("country")} placeholder={t("checkout.country")} required />
            <Textarea data-testid="checkout-note" value={form.note} onChange={set("note")} placeholder={t("checkout.note")} rows={3} />
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-border p-6 sm:p-8 sticky top-28">
              <h2 className="font-serif text-2xl text-wine mb-4">{t("checkout.summary")}</h2>
              <ul className="space-y-3 text-sm">
                {cart.map((c) => (
                  <li key={c.id} className="flex justify-between gap-3">
                    <span className="text-ink/70">{c.qty}× {c.name}</span>
                    <span className="font-medium shrink-0">CHF {(c.price * c.qty).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-border mt-4 pt-4">
                {discount ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 text-sm text-green-800" data-testid="discount-applied">
                    <span className="flex items-center gap-2"><Tag className="w-4 h-4" /> {discount.code} ({discount.type === "percent" ? `−${discount.value}%` : `−CHF ${discount.value.toFixed(2)}`})</span>
                    <button type="button" data-testid="discount-remove-button" onClick={() => setDiscount(null)} className="hover:text-green-950"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input data-testid="discount-code-input" value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder={t("checkout.codePlaceholder")} className="h-10" />
                    <Button type="button" data-testid="discount-apply-button" variant="outline" disabled={codeLoading || !codeInput.trim()} onClick={applyCode} className="h-10 border-wine/30 text-wine shrink-0">
                      {codeLoading ? "…" : t("checkout.codeApply")}
                    </Button>
                  </div>
                )}
                {codeError && <p className="mt-2 text-xs text-destructive" data-testid="discount-error">{codeError}</p>}
              </div>

              <div className="border-t border-border mt-4 pt-4 space-y-1">
                <div className="flex justify-between text-sm text-ink/60">
                  <span>{t("checkout.bottlesCount")}</span><span data-testid="checkout-bottle-count">{bottleCount}</span>
                </div>
                <div className="flex justify-between text-sm text-ink/60">
                  <span>{t("common.subtotal")}</span><span>CHF {cartTotal.toFixed(2)}</span>
                </div>
                {discount && (
                  <div className="flex justify-between text-sm text-green-700" data-testid="discount-line">
                    <span>{t("checkout.discount")} ({discount.code})</span><span>−CHF {discount.amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-serif text-xl text-ink">
                  <span>{t("common.total")}</span>
                  <span className="text-wine font-semibold" data-testid="checkout-total">CHF {finalTotal.toFixed(2)}</span>
                </div>
              </div>

              {tooFew && (
                <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800" data-testid="min-bottles-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {t("checkout.minWarning")}
                </div>
              )}
              {error && <p className="mt-4 text-sm text-destructive" data-testid="checkout-error">{error}</p>}

              {soldOut ? (
                <div className="mt-6" data-testid="checkout-soldout-notice">
                  <div className="flex items-start gap-2 bg-wine/5 border border-wine/20 rounded-xl px-4 py-3 text-sm text-wine">
                    <PackageX className="w-4 h-4 mt-0.5 shrink-0" />
                    {lang === "en"
                      ? "Currently sold out — our supplier is temporarily unable to deliver. Ordering is paused."
                      : "Momentan ausverkauft — unser Lieferant kann zurzeit leider nicht liefern. Bestellungen sind pausiert."}
                  </div>
                  <Button data-testid="checkout-waitlist-button" type="button" onClick={() => setWaitlistOpen(true)} className="w-full mt-4 bg-wine hover:bg-wine-dark text-white h-12 text-base gap-2 rounded-full">
                    <BellRing className="w-4 h-4" /> {lang === "en" ? "Notify me when available" : "Benachrichtigen, sobald lieferbar"}
                  </Button>
                  <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} lang={lang} />
                </div>
              ) : (
                <>
                  <Button data-testid="checkout-pay-button" type="submit" disabled={loading || tooFew} className="w-full mt-6 bg-wine hover:bg-wine-dark text-white h-12 text-base gap-2">
                    <Lock className="w-4 h-4" /> {loading ? t("checkout.paying") : t("checkout.pay")}
                  </Button>
                  <p className="mt-3 text-xs text-ink/40 text-center">{t("checkout.secure")}</p>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
